import { SampleReport } from './models';
export const SAMPLE_REPORTS: SampleReport[] = [
  {
    "slug": "sample-report-cardiology",
    "badge": "Anonymised Sample Report — For Demonstration Only",
    "spec": "Cardiology",
    "title": "Example Case: Coronary Artery Disease Treatment Decision",
    "sub": "Second Opinion Report",
    "caseRef": "SO-CARD-001",
    "quote": "“I've been told I need a procedure for my heart condition. Is this the right treatment, and are there alternatives?”",
    "history": [
      {
        "label": "Patient history",
        "text": "58-year-old male with chest discomfort on exertion, recently diagnosed with coronary artery disease."
      },
      {
        "label": "Previous diagnosis",
        "text": "Coronary artery disease identified on angiogram, with symptoms consistent with reduced blood flow on exertion."
      },
      {
        "label": "Symptoms",
        "text": "Chest discomfort with physical activity, improving with rest."
      },
      {
        "label": "Previous treatment",
        "text": "Medication (including antiplatelet and cholesterol-lowering therapy) and lifestyle changes."
      }
    ],
    "docsIntro": "This demonstrates that the platform reviews the actual medical records submitted, rather than relying on a short consultation alone.",
    "docs": [
      "ECG Report",
      "Echocardiogram Report",
      "Angiogram Report",
      "Medication History",
      "Previous Cardiology Consultation"
    ],
    "reviewList": [
      "Findings are consistent with the reported severity of coronary artery disease.",
      "Symptoms and cardiovascular risk factors are relevant to the management decision.",
      "Response to current medical therapy has been noted."
    ],
    "reviewPara": "The decision between continued medical therapy and a procedural option should weigh symptoms, test findings and overall cardiovascular risk together.",
    "options": [
      [
        "Lifestyle & risk factor management",
        "Reduce cardiovascular risk",
        "Appropriate alongside any other option"
      ],
      [
        "Optimised medical therapy",
        "Symptom and risk control",
        "May be sufficient depending on severity"
      ],
      [
        "Angioplasty / stenting",
        "Restore blood flow",
        "May be considered depending on angiogram findings"
      ],
      [
        "Bypass surgery",
        "Restore blood flow in more extensive disease",
        "Considered in selected cases based on assessment"
      ],
      [
        "Continued monitoring",
        "Track progression",
        "May be appropriate with regular review"
      ]
    ],
    "opinionLabel": "SPECIALIST OPINION",
    "opinion": "Based on the records reviewed, the reported findings are consistent with a condition where more than one management pathway may be reasonable. Whether medical therapy alone, or a procedural option, is more appropriate should be decided following a full clinical assessment and discussion of expected benefits and risks with your cardiology team.",
    "qa": [
      {
        "q": "Is the recommended treatment appropriate?",
        "a": "It is consistent with a reasonable approach for the reported findings — though alternative or additional options may also be worth discussing with your cardiologist."
      },
      {
        "q": "Can I try medication first?",
        "a": "Depending on your test results and symptoms, optimised medical therapy may be a reasonable option to discuss before considering a procedure."
      },
      {
        "q": "What are the risks of waiting?",
        "a": "This depends on your specific findings and symptom pattern — your cardiology team can advise based on ongoing review."
      },
      {
        "q": "What lifestyle changes matter most?",
        "a": "Risk factor management (activity, diet, smoking cessation where relevant, and blood pressure/cholesterol control) is appropriate regardless of which treatment path is chosen."
      }
    ],
    "steps": [
      "Cardiology consultation to review angiogram and test findings",
      "Discuss medical therapy versus procedural options",
      "Review cardiovascular risk factors",
      "Agree a monitoring or follow-up schedule",
      "Reassess if symptoms change"
    ],
    "disclaimer": "This sample is for demonstration of report format and does not constitute medical advice."
  },
  {
    "slug": "sample-report-oncology",
    "badge": "Anonymised Sample Report — For Demonstration Only",
    "spec": "Oncology",
    "title": "Example Case: Treatment Options Second Opinion",
    "sub": "Second Opinion Report",
    "caseRef": "SO-ONCO-001",
    "quote": "“I've been recommended a treatment plan for my diagnosis. Should I explore other treatment options before deciding?”",
    "history": [
      {
        "label": "Patient history",
        "text": "54-year-old recently diagnosed with an early-stage solid tumour following diagnostic workup."
      },
      {
        "label": "Previous diagnosis",
        "text": "Early-stage tumour, as characterised on biopsy and imaging, with a treatment plan proposed by the primary oncology team."
      },
      {
        "label": "Symptoms",
        "text": "Symptoms as reported at diagnosis; no treatment started at the time of this second opinion request."
      },
      {
        "label": "Previous treatment",
        "text": "Diagnostic workup completed; no treatment commenced yet."
      }
    ],
    "docsIntro": "This demonstrates that the platform reviews the actual medical records submitted, rather than relying on a short consultation alone.",
    "docs": [
      "Biopsy / Pathology Report",
      "Imaging Report (CT / MRI / PET)",
      "Oncology Consultation Notes",
      "Blood Test Results",
      "Previous Treatment History"
    ],
    "reviewList": [
      "Findings are consistent with the reported diagnosis and staging information provided.",
      "Treatment recommendations typically consider tumour type, stage and overall fitness.",
      "More than one evidence-based treatment pathway may be appropriate depending on individual factors."
    ],
    "reviewPara": "The choice between treatment approaches should be made with the treating oncology team, considering clinical goals and personal preference.",
    "options": [
      [
        "Surgery",
        "Remove or reduce tumour",
        "May be appropriate depending on stage and location"
      ],
      [
        "Chemotherapy",
        "Systemic treatment",
        "As clinically indicated by tumour type and stage"
      ],
      [
        "Radiotherapy",
        "Targeted local treatment",
        "May be considered alone or alongside other options"
      ],
      [
        "Targeted / other therapy",
        "Treatment based on tumour characteristics",
        "Depends on pathology and molecular findings"
      ],
      [
        "Active surveillance",
        "Monitor without immediate treatment",
        "May be appropriate in selected early-stage cases"
      ]
    ],
    "opinionLabel": "SPECIALIST OPINION",
    "opinion": "Based on the records reviewed, the recommended treatment approach is consistent with a reasonable management pathway for the reported diagnosis. Alternative or combined approaches may also be appropriate depending on individual clinical factors, and should be discussed with the treating oncology team before a final decision is made.",
    "qa": [
      {
        "q": "Should I seek another treatment opinion?",
        "a": "A second opinion can help confirm the recommended approach or surface reasonable alternatives — this report reflects that kind of independent review."
      },
      {
        "q": "Are there less invasive options?",
        "a": "Depending on stage and tumour characteristics, alternative approaches may be reasonable to discuss with your oncology team."
      },
      {
        "q": "How urgent is the decision?",
        "a": "Timing depends on your specific diagnosis and staging — your treating team can advise on an appropriate timeframe for deciding."
      },
      {
        "q": "What should I ask my oncologist next?",
        "a": "It's reasonable to ask about the goals of each option, expected outcomes, and how each would affect your day-to-day life."
      }
    ],
    "steps": [
      "Oncology consultation to discuss this second opinion",
      "Consider a multidisciplinary team review, if not already done",
      "Clarify the goals of treatment with your care team",
      "Ask about expected benefits and risks of each option",
      "Agree next steps and a follow-up plan with your treating oncologist"
    ],
    "disclaimer": "This sample is for demonstration of report format and does not constitute medical advice."
  },
  {
    "slug": "sample-report-orthopaedics",
    "badge": "Anonymised Sample Report — For Demonstration Only",
    "spec": "Orthopaedics",
    "title": "Example Case: Knee Replacement Decision",
    "sub": "Second Opinion Report",
    "caseRef": "SO-ORTHO-001",
    "quote": "“I have been advised to undergo knee replacement. Do I really need surgery now, and are there other options?”",
    "history": [
      {
        "label": "Patient history",
        "text": "62-year-old female with bilateral knee pain, left worse than right, for 3 years."
      },
      {
        "label": "Previous diagnosis",
        "text": "Severe osteoarthritis of the left knee with persistent pain and functional limitation despite conservative treatment."
      },
      {
        "label": "Symptoms",
        "text": "Difficulty walking and climbing stairs; symptoms affecting daily activities."
      },
      {
        "label": "Previous treatment",
        "text": "Physiotherapy, pain medication, lifestyle modification, and previous injections."
      }
    ],
    "docsIntro": "This demonstrates that the platform reviews the actual medical records submitted, rather than relying on a short consultation alone.",
    "docs": [
      "X-Ray Report — Both Knees",
      "MRI Report — Left Knee",
      "Previous Consultation",
      "Medication History",
      "Physiotherapy Report"
    ],
    "reviewList": [
      "Radiographic findings suggest advanced degenerative changes.",
      "Symptoms are affecting daily activities.",
      "Previous non-surgical treatment has provided limited benefit."
    ],
    "reviewPara": "The decision for surgery should consider symptoms, functional limitation, clinical examination and imaging findings together.",
    "options": [
      [
        "Continued conservative care",
        "Manage symptoms without surgery",
        "May be considered depending on symptoms"
      ],
      [
        "Physiotherapy / exercise",
        "Maintain strength and function",
        "Appropriate for ongoing management"
      ],
      [
        "Weight / lifestyle management",
        "Reduce joint load",
        "May support symptom management where applicable"
      ],
      [
        "Medication",
        "Symptom relief",
        "As clinically appropriate"
      ],
      [
        "Injection therapy",
        "Symptom relief in selected cases",
        "May be considered depending on clinical assessment"
      ],
      [
        "Knee replacement",
        "Definitive surgical treatment",
        "May be considered when symptoms and functional limitation remain significant despite appropriate conservative management"
      ]
    ],
    "opinionLabel": "SPECIALIST OPINION",
    "opinion": "Based on the records reviewed, knee replacement may be a reasonable option if symptoms remain significant despite appropriate non-surgical treatment. The final decision should be made following an appropriate clinical assessment and discussion of expected benefits, risks and alternatives.",
    "qa": [
      {
        "q": "Do I definitely need knee replacement?",
        "a": "Not necessarily on this evidence alone — it may be a reasonable option if symptoms remain significant despite appropriate conservative treatment, but the decision should follow a full clinical assessment."
      },
      {
        "q": "Can I postpone surgery?",
        "a": "Depending on your symptoms and functional limitation, continued conservative management may be a reasonable option to discuss with your orthopaedic team."
      },
      {
        "q": "Are injections or physiotherapy still useful?",
        "a": "Both may still have a role depending on clinical assessment, either as ongoing management or alongside a decision about surgery."
      },
      {
        "q": "What are the risks of waiting?",
        "a": "This depends on how your symptoms and function progress — your orthopaedic surgeon can advise based on regular review."
      },
      {
        "q": "If surgery is required, what type of replacement is appropriate?",
        "a": "The specific type of replacement, if surgery is chosen, should be determined by your orthopaedic surgeon based on examination and imaging."
      }
    ],
    "steps": [
      "Continue or consider recommended conservative management",
      "Orthopaedic clinical review to discuss findings",
      "Monitor pain and functional limitation",
      "Follow-up review at an appropriate interval",
      "Consider surgical consultation if symptoms remain significant"
    ],
    "disclaimer": "This sample is for demonstration of report format and does not constitute medical advice."
  }
];
